import { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import cx from 'classnames';
import StickyCTAHeader from './StickyCTAHeader';
import styles from './StickyCTA.module.scss';

const getStickToElementPosition = stickToElement => {
  if (!stickToElement) {
    return 0;
  }
  return Math.floor(
    stickToElement.getBoundingClientRect().top -
      document.body.getBoundingClientRect().top +
      stickToElement.offsetHeight
  );
};

const StickyCTA = ({
  expandOnStick = true,
  className = '',
  children,
  portalTarget,
  footer = '',
  header = ''
}) => {
  const [isSticky, setIsSticky] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const [currentHeight, setCurrentHeight] = useState(0);
  const isExpandable = Boolean(children);
  const ctaElementRef = useRef(null);
  const placeholderElementRef = useRef(null);

  useEffect(() => {
    const scrollHandler = () => {
      const currScrollBottom = Math.floor(window.scrollY + window.innerHeight);
      const footerPosition = getStickToElementPosition(
        placeholderElementRef.current
      );
      if (currScrollBottom >= footerPosition) {
        if (isExpandable && expandOnStick && isSticky) {
          setIsExpanded(true);
        }
        setIsSticky(false);
      } else {
        setIsSticky(true);
      }
    };
    document.addEventListener('scroll', scrollHandler);
    return () => {
      document.removeEventListener('scroll', scrollHandler);
    };
  }, [currentHeight, isSticky, isExpandable, isExpanded, expandOnStick]);

  const updateCurrentHeight = () => {
    setCurrentHeight(ctaElementRef.current?.offsetHeight ?? 0);
  };

  useEffect(() => {
    updateCurrentHeight();
  }, [isExpanded]);

  useEffect(() => {
    updateCurrentHeight();
    window.addEventListener('resize', updateCurrentHeight);
    return () => {
      window.removeEventListener('resize', updateCurrentHeight);
    };
  }, []);

  const stickySectionClassname = cx(
    styles['sticky-cta__container'],
    styles['cat-bg-default'],
    'w-100 p-3 d-flex flex-column',
    {
      [styles['sticky']]: isSticky
    }
  );
  const template = (
    <div
      className={cx('position-relative', className)}
      data-testid="sticky-cta"
    >
      <div className={stickySectionClassname} ref={ctaElementRef}>
        <StickyCTAHeader
          isExpandable={isExpandable}
          isExpanded={isExpanded}
          onExpanderClick={() => setIsExpanded(!isExpanded)}
        >
          {header}
        </StickyCTAHeader>
        <div
          className={cx('pt-2', isExpanded ? 'pb-3' : 'pb-2')}
          id="sticky-cta-content"
          aria-labelledby="sticky-cta-expand-btn"
        >
          {isExpanded && children}
        </div>
        {footer || ''}
      </div>
      <div
        className="w-100"
        ref={placeholderElementRef}
        style={{
          height: currentHeight
        }}
      />
    </div>
  );
  if (portalTarget) {
    return createPortal(
      template,
      typeof portalTarget === 'string'
        ? document.getElementById(portalTarget)
        : portalTarget
    );
  }
  return template;
};

StickyCTA.propTypes = {
  expandOnStick: PropTypes.bool,
  children: PropTypes.node,
  className: PropTypes.string,
  portalTarget: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  footer: PropTypes.element,
  header: PropTypes.element
};

export default StickyCTA;
